import type {
  CrossroadsLog,
  LogOverviewRow,
} from '@/api/hooks/useIntegrationLogs'
import { RoutingCell } from '@/components/Display/RoutingCell'
import { SeverityTag } from '@/components/Display/SeverityDisplay'
import { friendlySagaName } from '@/constants/sagaTypes'
import { formatTimestamp } from '@/utils/dateFormatters'
import { formatSagaEvent } from '@/utils/stringFormatters'
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  MinusCircleOutlined,
  TruckOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import {
  OrderLinkRenderer,
  myOrderNumber,
  partnerOrderNumber,
} from '@components/Grid/CellRenderers/OrderLinkRenderer'
import { BBDTag, GraviButton, Horizontal, Texto } from '@gravitate-js/excalibrr'
import {
  CellStyle,
  ColDef,
  ICellRendererParams,
  SetFilterValuesFuncParams,
} from 'ag-grid-community'
import { Tooltip } from 'antd'
import { useMemo } from 'react'
import { useLinkClickHandler } from 'react-router-dom'
import { getTimeElapsed } from '../utils/logUtils'
import { CheckpointSilencingMenu } from './CheckpointSilencingMenu'
import { CheckpointsDots } from './CheckpointsDots'
import { IssueCategoryCell } from './IssueCategoryCell'
import {
  ISSUE_CATEGORY_LABELS,
  issueCategoryLabel,
} from './LogDetailsPanel/categories'
import { SelectPageHeader } from './SelectPageHeader'

const issueCategoryName = (value: string) =>
  ISSUE_CATEGORY_LABELS[value] ?? value

/** colIds of the cells that open a log in the details sidebar. Clicking any
 * other cell expands/collapses the row's detail grid instead — see
 * useLogSidebar. */
export const LATEST_EVENT_COL_ID = 'latest_event'
export const LATEST_DETAILS_COL_ID = 'latest_details'
export const LATEST_ISSUE_COL_ID = 'latest_issue'
export const FIRST_ISSUE_COL_ID = 'first_issue'

/** The expand/collapse chevron column — AG Grid's group cell renderer
 * already toggles the row itself, so useLogSidebar leaves it alone rather
 * than toggling a second time and cancelling it out. */
export const EXPAND_COL_ID = 'expand'

/** Set-filter values come from the endpoint now, so the dropdown offers what
 * the whole result set holds rather than what the loaded page happens to show.
 * The server matches the raw stored value, so that's what goes in the filter
 * model; `format` only changes what's read. */
function serverSetFilter(
  colId: string,
  format: (value: string) => string = (value) => value,
): Pick<ColDef<LogOverviewRow>, 'filter' | 'filterParams'> {
  return {
    filter: 'agSetColumnFilter',
    filterParams: {
      values: (params: SetFilterValuesFuncParams<LogOverviewRow>) => {
        const getOptions = params.context?.getFilterOptions
        if (!getOptions) return params.success([])
        getOptions(colId, params.api.getFilterModel()).then(
          (values: (string | null)[]) => params.success(values),
        )
      },
      valueFormatter: (params: { value: string | null }) =>
        params.value ? format(params.value) : '(Blanks)',
      refreshValuesOnOpen: true,
    },
  }
}

/** The pinned silencing menu — its own click handling opens the dropdown, so
 * useLogSidebar leaves it alone rather than expanding the row underneath. */
export const SILENCING_COL_ID = 'silencing'

// Function to transform column definitions for multi-column sorting
function columnDefTransform(
  col: ColDef<LogOverviewRow>,
): ColDef<LogOverviewRow> {
  switch (col.field) {
    case 'updated_on':
      return { ...col, sort: 'desc', sortIndex: 0 }
    default:
      return col
  }
}

/** cellStyle for a fully-overwritten supply-plan-batch row -- grayscale +
 * faded, same opacity useLogDetailGrid.tsx uses for a retried log. Applied
 * per-cell (see useSupplyPlanLogColumnDefs) rather than via getRowStyle:
 * a row-level filter/opacity can't be selectively undone by one cell (it
 * composites the whole row's rendered output as a unit), so the Status
 * column is excluded by simply never getting this cellStyle -- the
 * "Overwritten" chip itself should stay legible at a glance. */
function dimIfOverwritten(params: {
  data?: LogOverviewRow
}): CellStyle | undefined {
  return params.data?.rollup_status === 'overwritten'
    ? { filter: 'grayscale(0.6)', opacity: 0.65 }
    : undefined
}

/** Fades every cell of an order row whose checkpoints are all silenced, to the
 * same degree as the individual dulled dots. Per-cell for the same reason as
 * dimIfOverwritten: the silencing menu is excluded so it stays legible and
 * clickable, which is the only way back out of this state.
 *
 * A cellClassRule rather than a cellStyle because silencing only changes the
 * row's `checkpoints` -- AG Grid re-applies cellStyle only to cells whose own
 * value changed, so every other column would keep a stale opacity until an
 * unrelated redraw. Class rules are re-evaluated on every row data change. */
const dimIfFullySilenced: NonNullable<
  ColDef<LogOverviewRow>['cellClassRules']
> = {
  'silenced-log-cell': (params) => {
    const checkpoints = params.data?.checkpoints
    return (
      checkpoints?.type === 'OrderIntegrationCheckpoints' &&
      checkpoints.fully_silenced
    )
  },
}

// --- Column defs shared across every log type (order, supply plan, ...) ---
// None of these read order-specific metadata, so they work unmodified for
// any LogOverviewRow regardless of metadata.type.

const expandColumnDef: ColDef<LogOverviewRow> = {
  headerName: '',
  colId: EXPAND_COL_ID,
  sortable: false,
  filter: false,
  pinned: 'left',
  width: 50,
  maxWidth: 50,
  minWidth: 50,
  cellRenderer: 'agGroupCellRenderer',
  cellRendererParams: {
    suppressCount: true,
    suppressDoubleClickExpand: false,
  },
  suppressSizeToFit: true,
  resizable: false,
}

/** Row-click already opens this log in the details sidebar, which surfaces
 * the same link — this column just saves that extra step. Empty for log
 * types with no corresponding grid to link to (e.g. orders). Icon matches
 * the Supply Plans page's nav icon. */
function SupplyPlanBatchLinkButton({ batchId }: { batchId: string }) {
  const url = `/operations/supply-plans?batch_id=${encodeURIComponent(batchId)}`
  const handleLinkClick = useLinkClickHandler<HTMLElement>(url)
  return (
    <Tooltip title='View Supply Plans'>
      <GraviButton
        size='middle'
        appearance='text'
        href={url}
        icon={
          <TruckOutlined
            style={{ color: 'hsl(var(--primary))', fontSize: 18 }}
          />
        }
        onClick={(e) => {
          e.stopPropagation()
          handleLinkClick(e)
        }}
      />
    </Tooltip>
  )
}

const batchLinkColumnDef: ColDef<LogOverviewRow> = {
  headerName: '',
  colId: 'batch_link',
  pinned: 'left',
  width: 56,
  maxWidth: 56,
  minWidth: 56,
  sortable: false,
  filter: false,
  suppressSizeToFit: true,
  resizable: false,
  cellRenderer: (params: ICellRendererParams<LogOverviewRow>) => {
    const metadata = params.data?.metadata
    if (metadata?.type !== 'supply_plan_batch') return null
    return <SupplyPlanBatchLinkButton batchId={metadata.batch_id} />
  },
}

const latestTimeColumnDef: ColDef<LogOverviewRow> = {
  headerName: 'Log Time',
  field: 'updated_on',
  width: 240,
  initialFlex: 0,
  suppressSizeToFit: true,
  sortable: true,
  filter: 'agDateColumnFilter',
  cellRenderer: (params: ICellRendererParams<LogOverviewRow>) => {
    return (
      <Horizontal gap={8} alignItems='center'>
        <Texto>{formatTimestamp(params.value)}</Texto>
        <Texto appearance='hint' style={{ marginLeft: 'auto' }}>
          {getTimeElapsed(params.value)}
        </Texto>
      </Horizontal>
    )
  },
}

/** Opens a checkpoint's latest event in the details sidebar. Only the grids
 * that own a sidebar pass one; without it the popover's checkpoint rows stay
 * inert. */
export type CheckpointLogSelectHandler = (
  log: CrossroadsLog,
  row: LogOverviewRow,
) => void

/** A factory rather than a constant because the checkpoint rows in the dots'
 * popover open their saga's latest event in the details sidebar, which is
 * state the grid owns -- see useLogSidebar. */
function checkpointsColumnDef(
  onLogSelected?: CheckpointLogSelectHandler,
): ColDef<LogOverviewRow> {
  return {
    headerName: 'Checkpoints',
    field: 'checkpoints',
    width: 180,
    initialFlex: 0,
    sortable: false,
    filter: false,
    cellRenderer: (params: ICellRendererParams<LogOverviewRow>) => {
      const row = params.data
      if (!row?.checkpoints) return null
      return (
        <CheckpointsDots
          checkpoints={row.checkpoints}
          logGroupId={row.id}
          onLogSelected={
            onLogSelected ? (log) => onLogSelected(log, row) : undefined
          }
        />
      )
    },
  }
}

/** Order logs only — /set_order_silencing rejects a group whose checkpoints
 * aren't OrderIntegrationCheckpoints. */
const silencingColumnDef: ColDef<LogOverviewRow> = {
  headerName: '',
  colId: SILENCING_COL_ID,
  // Zeroes .ag-cell's padding so the whole cell is the menu's click target,
  // not just the dots -- see styles/overrides.less.
  cellClass: 'silencing-cell',
  // minWidth is explicit because the theme's headerCellMinWidth would
  // otherwise clamp the column wider than this.
  width: 36,
  minWidth: 36,
  maxWidth: 36,
  initialFlex: 0,
  pinned: 'right',
  sortable: false,
  filter: false,
  resizable: false,
  suppressMenu: true,
  cellRenderer: (params: ICellRendererParams<LogOverviewRow>) => {
    if (!params.data) return null
    return (
      <CheckpointSilencingMenu
        row={params.data}
        onSaved={params.context?.applySilencing}
        onClosed={params.context?.refreshGrid}
      />
    )
  },
}

const eventIdColumnDef: ColDef<LogOverviewRow> = {
  headerName: 'Event ID',
  colId: 'event_id',
  hide: true,
  width: 170,
  initialFlex: 0,
  ...serverSetFilter('event_id', (value) => friendlySagaName(value) ?? value),
  valueGetter: (params) => {
    const log = params.data?.latest_log
    const formatted = formatSagaEvent(log?.saga_type, log?.saga_id)
    if (!formatted) return null
    return formatted.shortId
      ? `${formatted.typeLabel} (${formatted.shortId})`
      : formatted.typeLabel
  },
  cellRenderer: (params: ICellRendererParams<LogOverviewRow>) => {
    const log = params.data?.latest_log
    const formatted = formatSagaEvent(log?.saga_type, log?.saga_id)
    if (!formatted) return null
    return (
      <Tooltip title={formatted.fullId || 'No saga ID'}>
        <Texto>
          {formatted.typeLabel}
          {formatted.shortId && (
            <Texto
              appearance='hint'
              style={{ marginLeft: 4, fontFamily: 'monospace' }}
            >
              ({formatted.shortId})
            </Texto>
          )}
        </Texto>
      </Tooltip>
    )
  },
}

/** Pointer cursor on every cell that opens the details sidebar, to set it
 * apart from the expand-the-row default. */
const CLICKABLE_TEXT_STYLE = { cursor: 'pointer' }

/** Same wording the Status columns' chips use. */
const LOG_STATUS_LABELS: Record<string, string> = {
  succeeded: 'Success',
  failed: 'Failed',
  warning: 'Warning',
  unknown: 'In Progress',
}

/** "<friendly saga name> - <status>", e.g. "Order Creation - Failed". Falls
 * back to whichever half is known. */
function latestEventLabel(log: LogOverviewRow['latest_log']): string | null {
  if (!log) return null
  const saga = friendlySagaName(log.saga_type)
  const status = LOG_STATUS_LABELS[log.status] ?? log.status
  if (!saga) return status ?? null
  return status ? `${saga} - ${status}` : saga
}

const latestEventColumnDef: ColDef<LogOverviewRow> = {
  headerName: 'Latest Event',
  colId: LATEST_EVENT_COL_ID,
  valueGetter: (params) => latestEventLabel(params.data?.latest_log),
  flex: 1,
  minWidth: 240,
  filter: false,
  cellRenderer: (params: ICellRendererParams<LogOverviewRow>) => {
    if (!params.value) return null
    return <Texto style={CLICKABLE_TEXT_STYLE}>{params.value}</Texto>
  },
}

const latestDetailsColumnDef: ColDef<LogOverviewRow> = {
  headerName: 'Latest Log Details',
  colId: LATEST_DETAILS_COL_ID,
  hide: true,
  filter: 'agTextColumnFilter',
  valueGetter: (params) => params.data?.latest_log?.detail?.message ?? null,
  flex: 1,
  minWidth: 240,
  cellRenderer: (params: ICellRendererParams<LogOverviewRow>) => {
    if (!params.value) return null
    return (
      <Tooltip title={params.value}>
        <Texto style={CLICKABLE_TEXT_STYLE}>{params.value}</Texto>
      </Tooltip>
    )
  },
}

const latestIssueColumnDef: ColDef<LogOverviewRow> = {
  headerName: 'Latest Issue',
  colId: LATEST_ISSUE_COL_ID,
  width: 200,
  initialFlex: 0,
  sortable: false,
  ...serverSetFilter('latest_issue', issueCategoryName),
  valueGetter: (params) => issueCategoryLabel(params.data?.last_open_issue),
  cellRenderer: (params: ICellRendererParams<LogOverviewRow>) => (
    <IssueCategoryCell log={params.data?.last_open_issue} />
  ),
}

const firstIssueColumnDef: ColDef<LogOverviewRow> = {
  headerName: 'First Issue',
  colId: FIRST_ISSUE_COL_ID,
  width: 200,
  initialFlex: 0,
  sortable: false,
  ...serverSetFilter('first_issue', issueCategoryName),
  valueGetter: (params) => issueCategoryLabel(params.data?.first_open_issue),
  cellRenderer: (params: ICellRendererParams<LogOverviewRow>) => (
    <IssueCategoryCell log={params.data?.first_open_issue} />
  ),
}

const statusColumnDef: ColDef<LogOverviewRow> = {
  headerName: 'Status',
  field: 'rollup_status',
  width: 110,
  initialFlex: 0,
  suppressSizeToFit: true,
  sortable: true,
  ...serverSetFilter(
    'rollup_status',
    (value) => LOG_STATUS_LABELS[value] ?? value,
  ),
  cellRenderer: (params: ICellRendererParams<LogOverviewRow>) => {
    if (!params.data) return null
    const status = params.data.rollup_status

    if (status === 'failed') {
      return (
        <BBDTag error style={{ width: 'fit-content' }}>
          <ExclamationCircleOutlined /> Failed
        </BBDTag>
      )
    } else if (status === 'warning') {
      return (
        <BBDTag warning style={{ width: 'fit-content' }}>
          <WarningOutlined /> Warning
        </BBDTag>
      )
    } else if (status === 'succeeded') {
      return (
        <BBDTag success style={{ width: 'fit-content' }}>
          <CheckCircleOutlined /> Success
        </BBDTag>
      )
    } else if (status === 'partial_success') {
      // Some, but not all, of the batch's supply plans made it through this
      // stage (e.g. 1/3 mapped) -- a completed, mixed outcome, not an
      // in-flight one, so it gets its own label rather than falling into
      // the "In Progress" catch-all below.
      return (
        <BBDTag warning style={{ width: 'fit-content' }}>
          <WarningOutlined /> Partial Success
        </BBDTag>
      )
    } else if (status === 'overwritten') {
      // Every plan in this batch has since been reclaimed by a later batch --
      // not a failure, just stale, so a neutral (non-error/warning) tag.
      return (
        <BBDTag style={{ width: 'fit-content' }}>
          <MinusCircleOutlined /> Overwritten
        </BBDTag>
      )
    } else {
      return <BBDTag style={{ width: 'fit-content' }}>In Progress</BBDTag>
    }
  },
}

const severityColumnDef: ColDef<LogOverviewRow> = {
  headerName: 'Severity',
  colId: 'severity',
  width: 120,
  initialFlex: 0,
  ...serverSetFilter(
    'severity',
    (value) => value.charAt(0).toUpperCase() + value.slice(1),
  ),
  valueGetter: (params) => params.data?.latest_log?.severity ?? null,
  comparator: (sevA, sevB) => {
    const severityOrder: Record<string, number> = {
      high: 3,
      medium: 2,
      low: 1,
    }
    return (severityOrder[sevA] || 0) - (severityOrder[sevB] || 0)
  },
  cellRenderer: (params: ICellRendererParams<LogOverviewRow>) => {
    if (params.data?.rollup_status === 'succeeded') return null
    return (
      <SeverityTag severity={params.value} style={{ width: 'fit-content' }} />
    )
  },
}

const routingColumnDef: ColDef<LogOverviewRow> = {
  headerName: 'Routing',
  colId: 'routing',
  ...serverSetFilter('routing'),
  valueGetter: (params) => {
    const routing = params.data?.metadata?.routing
    if (!routing) return null
    return `${routing.origin_tenant_name} → ${routing.destination_tenant_name}`
  },
  flex: 1,
  minWidth: 200,
  cellRenderer: (params: ICellRendererParams<LogOverviewRow>) => (
    <RoutingCell routing={params.data?.metadata?.routing} />
  ),
}

/** Column defs shared across every log type (order, supply plan, ...). */
const commonLogColumnDefs: ColDef<LogOverviewRow>[] = [
  expandColumnDef,
  batchLinkColumnDef,
  latestTimeColumnDef,
  checkpointsColumnDef(),
  statusColumnDef,
  severityColumnDef,
  latestEventColumnDef,
  latestDetailsColumnDef,
  latestIssueColumnDef,
  firstIssueColumnDef,
  routingColumnDef,
  eventIdColumnDef,
]

function orderMetadata(row: LogOverviewRow | undefined) {
  return row?.metadata.type === 'order' ? row.metadata : null
}

/** Columns for order-type logs only — everything else comes from
 * commonLogColumnDefs. */
export function useLogColumnDefs(
  tenantName: string,
  onCheckpointLogSelected?: CheckpointLogSelectHandler,
) {
  return useMemo(() => {
    const orderColumnDefs: ColDef<LogOverviewRow>[] = [
      {
        headerName: 'Order',
        colId: 'order',
        width: 200,
        initialFlex: 0,
        filter: false,
        valueGetter: (params) => {
          const metadata = orderMetadata(params.data)
          return myOrderNumber(metadata?.order, metadata?.routing, tenantName)
        },
        cellRenderer: (params: ICellRendererParams<LogOverviewRow>) => {
          const metadata = orderMetadata(params.data)
          return (
            <OrderLinkRenderer
              order_key={metadata?.order}
              routing={metadata?.routing}
              myNumber={true}
            />
          )
        },
      },
      {
        headerName: 'Partner Order #',
        colId: 'partner_order',
        width: 200,
        initialFlex: 0,
        filter: false,
        valueGetter: (params) => {
          const metadata = orderMetadata(params.data)
          return partnerOrderNumber(
            metadata?.order,
            metadata?.routing,
            tenantName,
          )
        },
        cellRenderer: (params: ICellRendererParams<LogOverviewRow>) => {
          const metadata = orderMetadata(params.data)
          return (
            <OrderLinkRenderer
              order_key={metadata?.order}
              routing={metadata?.routing}
              myNumber={false}
            />
          )
        },
      },
    ]

    // Selection checkbox lives beside the group arrow, and only on the order
    // grid -- the supply-plan grid has no bulk action to feed.
    const selectableExpandColumnDef: ColDef<LogOverviewRow> = {
      ...expandColumnDef,
      cellRendererParams: {
        ...(expandColumnDef.cellRendererParams as object),
        checkbox: true,
      },
      headerComponent: SelectPageHeader,
      // Wider than the shared def to fit the checkbox alongside the arrow.
      width: 80,
      minWidth: 80,
      maxWidth: 80,
    }

    return [
      selectableExpandColumnDef,
      latestTimeColumnDef,
      ...orderColumnDefs,
      checkpointsColumnDef(onCheckpointLogSelected),
      statusColumnDef,
      severityColumnDef,
      latestEventColumnDef,
      latestDetailsColumnDef,
      latestIssueColumnDef,
      firstIssueColumnDef,
      routingColumnDef,
      eventIdColumnDef,
      silencingColumnDef,
    ]
      .map(columnDefTransform)
      .map((col) =>
        col.colId === SILENCING_COL_ID
          ? col
          : { ...col, cellClassRules: dimIfFullySilenced },
      )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantName, onCheckpointLogSelected])
}

/** Columns for supply-plan-type logs — just the shared set, nothing
 * order-specific. */
export function useSupplyPlanLogColumnDefs() {
  return useMemo(
    () =>
      commonLogColumnDefs.map((col) =>
        columnDefTransform(
          // rollup_status is the Status column itself -- see
          // dimIfOverwritten's comment for why it's the one column that
          // doesn't get this treatment.
          col.field === 'rollup_status'
            ? col
            : { ...col, cellStyle: dimIfOverwritten },
        ),
      ),
    [],
  )
}
